import {
    Alert,
    AlertTitle,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tabs,
    Typography
} from '@mui/material';
import React from 'react';

import InfoIcon from '@mui/icons-material/Info';
import {
    flexRender,
    getCoreRowModel,
    getFacetedMinMaxValues,
    getFacetedRowModel,
    getFacetedUniqueValues,
    getFilteredRowModel,
    getSortedRowModel,
    useReactTable
} from '@tanstack/react-table';
import Axios from 'axios';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ActionsCell } from './ActionCell';
import CompletedTaskDashboard from './CompletedTaskDashboard';
import FollowUpDashboard from './FollowUpDashboard';

import { DateTime, Settings } from 'luxon';
import getCookie from '../../utils/getCookies';

Settings.defaultZone = 'America/New_York';

dayjs.extend(utc);
dayjs.extend(timezone);

export default function TaskDashboard() {
    const [tasks, setTasks] = useState([]);
    const [inquiryTypeFilter, setInquiryTypeFilter] = useState('all');
    const [showPriorityOnly, setShowPriorityOnly] = useState(false);
    const [claimStatusFilter, setClaimStatusFilter] = useState('all');
    const [sorting, setSorting] = useState([{ id: 'requested_at', desc: true }]);
    const [action, setAction] = useState(0);
    const [actionCompleted, setActionCompleted] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [lastUpdate, setLastUpdate] = useState(Date.now());
    const [connectionStatus, setConnectionStatus] = useState('Connecting...');
    const [activeTab, setActiveTab] = useState('scheduled');
    const [completedTasks, setCompletedTasks] = useState([]);
    const [currentUser, setCurrentUser] = useState(null);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [editingTask, setEditingTask] = useState(null);
    const [emailPreviewOpen, setEmailPreviewOpen] = useState(false);
    const [emailPreviewContent, setEmailPreviewContent] = useState('');
    const [emailPreviewType, setEmailPreviewType] = useState('');
    const navigate = useNavigate();

    const handleEditClose = () => {
        setEditDialogOpen(false);
        setEditingTask(null);
    };

    useEffect(()=>{
        const fetchData = async () => {
            const fetchedTasks = await fetchTasks();
        };
        fetchData();
    }, [action]);

    useEffect(()=>{
        const fetchedCompletedTasks = fetchCompletedTasks();
        setCompletedTasks(fetchedCompletedTasks);
    }, [actionCompleted]);

    const handleEditSave = async () => {
        try {
            const token = getCookie('_auth');

            const updates = {
                first_email_scheduled: editingTask.first_email_scheduled,
                second_email_scheduled: editingTask.second_email_scheduled
            };
            
            const res = await Axios.patch(
                `${process.env.REACT_APP_URL}/api/v1/tasks/edit/${editingTask.id}`,
                updates,
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            );
            if(res.status === 200){
                handleEditClose();
                setAction(prev => prev + 1);
            }
        } catch (error) {
            console.error('[Edit] Error:', error);
            setError('Failed to update task');
        }
    };

    useEffect(() => {
        const fetchCurrentUser = async () => {
            const token = getCookie('_auth');
            const response = await Axios.get(`${process.env.REACT_APP_URL}/api/v1/admins/current-user`, {
                headers: {
                Authorization: `Bearer ${token}`,
            },
            });
            setCurrentUser(response.data);
        };
        fetchCurrentUser();
    }, []);

    const handleEmailPreviewOpen = (type) => {
        let content = '';
        if (type === 'first') {
            content = `
                <p>안녕하세요.</p>
                <br/>
                <p>A ONE 학원입니다.</p>
                <p>먼저 저희에게 소중한 문의를 주셔서 감사드립니다.</p>
                <br/>
                <p>현재 저희 실장님들과 매니저님들은 근무시간 이후에도 밤 늦게까지 모든 문의에 신속히 답변드리기 위해 최선을 다하고 있습니다.</p>
                <p>그러나 문의량이 많아 모든 요청에 즉시 답변드리기 어려운 상황인 점, 너그러운 양해 부탁드립니다.</p>
                <br/>
                <p>문의하신 내용은 저희가 잘 접수하였으며, 모든 요청은 순차적으로 처리하고 있습니다.</p>
                <p>늦어도 7일 이내에 정확하고 신중한 답변을 받으실 수 있도록 최선을 다하겠습니다.</p>
                <br/>
                <p>기다려주시는 동안 불편을 끼쳐드려 다시한번 죄송하다는 말씀 전합니다.</p>
                <p>저희를 믿고 기다려주시는 데에 깊이 감사드리며, 기대하시는 만큼 만족스러운 답변을 드리기 위해 더욱 노력하겠습니다.</p>
                <p>조금만 기다려 주세요.</p>
                <p>감사합니다.</p>
                <br/>
                <p>Best regards,</p>
                <p>A ONE Institute</p>
                <br/>
            `;
            content += '<img src="https://aone-universe.s3.amazonaws.com/aone-images/logo(no-background).png" alt="A ONE Institute" width="200px" height="39px">';

            content += '<div>'
                + '<font color="#666666"><font face="Arial, sans-serif">T.&nbsp;&nbsp;</font></font>'
                + '<font face="Arial, sans-serif" style="color:rgb(102,102,102)">201-266-8882&nbsp;</font>'
                + '<span style="color:rgb(102,102,102);font-family:바탕,serif"><font size="1">┃</font></span>'
                + '<font face="Arial, sans-serif" style="color:rgb(102,102,102)">&nbsp;201-346-<wbr>5689</font>'
                + '<font color="#666666"><span style="font-family:Arial,sans-serif">&nbsp;</span></font>'
                + '<font face="Arial, sans-serif" style="color:rgb(102,102,102)">&nbsp;</font>'
                + '<span style="color:rgb(102,102,102);font-family:바탕,serif"><font size="1">┃&nbsp;</font></span>'
                + '<span style="color:rgb(102,102,102);font-family:Arial,sans-serif">&nbsp;E.&nbsp;'
                + '<a href="mailto:aone@a-oneinstitute.com" target="_blank">aone@a-oneinstitute.com</a></span>'
                + '</div>';

            content += '<div>'
                + '<font color="#666666">'
                + '<span style="font-family:Arial,sans-serif">449 Broad Ave. Unit 2-1 &amp; 2-5, Palisades Park, NJ 07650</span>'
                + '</font>'
                + '</div>';

            content += '<div><font color="#666666"><span style="font-family:Arial,sans-serif">'
                + '<a href="https://www.a-oneinstitute.com" target="_blank" data-saferedirecturl="https://www.google.com/url?q=https://www.a-oneinstitute.com&amp;source=gmail&amp;ust=1693706048365000&amp;usg=AOvVaw20fPhTzBitxyJxCvPo0AsO">www.a-oneinstitute.com</a>'
                + '</span></font></div>';

            content += '<div><font color="#666666"><span style="font-family:Arial,sans-serif"><br></span></font></div>';
        } else if (type === 'second') {
            content = `
                <p>안녕하세요,</p>
                <br/>
                <p>A ONE 학원 입니다.</p>
                <br/>
                <p>먼저, 오랜 시간 기다리시게 하여 진심으로 죄송하다는 말씀드립니다.</p>
                <p>저희가 순차적으로 답변을 드리고 있어서요, 조금만 더 기다려주시면 빠른 시일 내에 답변드리겠습니다.</p>
                <br/>
                <p>문의주신 내용에 대해서는 오래 기다려주신 만큼 성심성의껏 답변드리기 위해 저희 실장님들을 비롯한 모든 매니저분들이 힘쓰고 계십니다.</p>
                <p>조금만 더 기다려주시면, 성의있는 답변을 전달받으실 수 있도록 하겠습니다.</p>
                <br/>
                <p>오랜시간 기다려주심에 다시 한번 감사와 사과의 말씀을 드립니다.</p>
                <br/>
                <p>감사합니다.</p>
                <br/>
                <p>Best regards,</p>
                <p>A ONE Institute</p>
                <br/>
            `;
            content += '<img src="https://aone-universe.s3.amazonaws.com/aone-images/logo(no-background).png" alt="A ONE Institute" width="200px" height="39px">';

            content += '<div>'
                + '<font color="#666666"><font face="Arial, sans-serif">T.&nbsp;&nbsp;</font></font>'
                + '<font face="Arial, sans-serif" style="color:rgb(102,102,102)">201-266-8882&nbsp;</font>'
                + '<span style="color:rgb(102,102,102);font-family:바탕,serif"><font size="1">┃</font></span>'
                + '<font face="Arial, sans-serif" style="color:rgb(102,102,102)">&nbsp;201-346-<wbr>5689</font>'
                + '<font color="#666666"><span style="font-family:Arial,sans-serif">&nbsp;</span></font>'
                + '<font face="Arial, sans-serif" style="color:rgb(102,102,102)">&nbsp;</font>'
                + '<span style="color:rgb(102,102,102);font-family:바탕,serif"><font size="1">┃&nbsp;</font></span>'
                + '<span style="color:rgb(102,102,102);font-family:Arial,sans-serif">&nbsp;E.&nbsp;'
                + '<a href="mailto:aone@a-oneinstitute.com" target="_blank">aone@a-oneinstitute.com</a></span>'
                + '</div>';

            content += '<div>'
                + '<font color="#666666">'
                + '<span style="font-family:Arial,sans-serif">449 Broad Ave. Unit 2-1 &amp; 2-5, Palisades Park, NJ 07650</span>'
                + '</font>'
                + '</div>';

            content += '<div><font color="#666666"><span style="font-family:Arial,sans-serif">'
                + '<a href="https://www.a-oneinstitute.com" target="_blank" data-saferedirecturl="https://www.google.com/url?q=https://www.a-oneinstitute.com&amp;source=gmail&amp;ust=1693706048365000&amp;usg=AOvVaw20fPhTzBitxyJxCvPo0AsO">www.a-oneinstitute.com</a>'
                + '</span></font></div>';

            content += '<div><font color="#666666"><span style="font-family:Arial,sans-serif"><br></span></font></div>';
        }
        setEmailPreviewContent(content);
        setEmailPreviewType(type);
        setEmailPreviewOpen(true);
    };

    const EmailPreviewDialog = () => (
        <Dialog 
            open={emailPreviewOpen} 
            onClose={() => setEmailPreviewOpen(false)}
            maxWidth="md"
            fullWidth
        >
            <DialogTitle>
                {emailPreviewType === 'first' ? '1차 안내 딜레이 템플릿' : '2차 안내 딜레이 템플릿'}
            </DialogTitle>
            <DialogContent>
                <Typography variant="body1" sx={{ mb: 2, fontWeight: 'bold' }}>    
                    The following email is sent if the task is not completed {emailPreviewType === 'first' ? 'within same day at 10:30 PM' : 'within 1 week'}.
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <div dangerouslySetInnerHTML={{ __html: emailPreviewContent }} />
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setEmailPreviewOpen(false)}>Close</Button>
            </DialogActions>
        </Dialog>
    );

    const columns = useMemo(
        () => [
            {
                header: 'No.',
                cell: ({ row }) => {
                    return row.index + 1;
                }
            },
            {
                header: 'Type',
                cell: ({ row }) => {
                    const { memo_id, is_info_mail } = row.original;
                    if (memo_id !== null && is_info_mail === false) {
                        return '재문의';
                    } else if (is_info_mail === true) {
                        return '신규문의';
                    }
                    return '-';
                },
            },
            {
                header: '이메일 코드',
                accessorKey: 'student.email_code',
            },
            {
                header: '안내번호',
                accessorKey: 'student.consultation_number',
                cell: ({ row }) => (
                    <Button
                        onClick={() => navigate(`/admin/consultationProgress?consultation_number=${row.original.student?.consultation_number}&student_card=false`)}
                        sx={{ textTransform: 'none' }}
                        color="primary"
                    >
                        {row.original.student?.consultation_number}
                    </Button>
                ),
            },
            {
                header: '요청일',
                accessorKey: 'requested_at',
                cell: ({ getValue }) => {
                    const requestDate = DateTime.fromISO(getValue());
                    return requestDate.setZone('America/New_York').toLocaleString(DateTime.DATE_SHORT);
                },
            },
            {
                header: () => (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        1차 안내 딜레이
                        <IconButton
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleEmailPreviewOpen('first');
                            }}
                        >
                            <InfoIcon sx={{ fontSize: 16, color: 'action.active' }} />
                        </IconButton>
                    </Box>
                ),
                accessorKey: 'first_email_sent_at',
                cell: ({ getValue, row }) => {
                    if (!row.original.first_email_scheduled) {
                        return 'Cancelled';
                    }

                    const firstEmailSent = getValue();
                    const requestDate = DateTime.fromISO(row.original.requested_at);
                    let scheduleDate;
                    if (requestDate.setZone('America/New_York').hour >= 23 || (requestDate.setZone('America/New_York').hour === 22 && requestDate.setZone('America/New_York').minute >= 30)) {
                        scheduleDate = requestDate.setZone('America/New_York').plus({ days: 1 }).set({ hour: 22, minute: 30, second: 0 });
                    } else {
                        scheduleDate = requestDate.setZone('America/New_York').set({ hour: 22, minute: 30, second: 0 });
                    }
                    
                    //Check if first email has been sent
                    if (firstEmailSent) {
                        return DateTime.fromISO(firstEmailSent).setZone('America/New_York').toLocaleString(DateTime.DATETIME_SHORT) + ' ✅';
                    }

                    const diffMinutes = scheduleDate.diffNow('minutes').minutes;
                    if (diffMinutes <= 0) {
                        return scheduleDate.setZone('America/New_York').toLocaleString(DateTime.DATETIME_SHORT) + ' ❌';
                    }
                    
                    let timeLeft;
                    if (diffMinutes > 1440) {
                        const days = Math.floor(diffMinutes / 1440);
                        timeLeft = ` (${days}d remaining)`;
                    } else {
                        const hoursLeft = Math.floor(diffMinutes / 60);
                        const minutesLeft = (diffMinutes % 60).toFixed(0);
                        timeLeft = ` (${hoursLeft}h ${minutesLeft}m remaining)`;
                    }
    
                    return requestDate.setZone('America/New_York').toLocaleString(DateTime.DATETIME_SHORT) + timeLeft;
                },
            },
            {
                header: () => (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        2차 안내 딜레이
                        <IconButton
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleEmailPreviewOpen('second');
                            }}
                        >
                            <InfoIcon sx={{ fontSize: 16, color: 'action.active' }} />
                        </IconButton>
                    </Box>
                ),
                accessorKey: 'second_email_sent_at',
                cell: ({ getValue, row }) => {
                    if (!row.original.second_email_scheduled) {
                        return 'Cancelled';
                    }

                    const requestDate = DateTime.fromISO(row.original.requested_at);
                    const scheduleDate = requestDate.setZone('America/New_York').plus({ days: 7 }).set({ hour: 22, minute: 30, second: 0 });
                    const secondEmailSent = getValue();

                    //Check if second email has been sent
                    if (secondEmailSent) {
                        return DateTime.fromISO(secondEmailSent).setZone('America/New_York').toLocaleString(DateTime.DATETIME_SHORT) + ' ✅';
                    }

                    const diffMinutes = scheduleDate.diffNow('minutes').minutes;
                    if (diffMinutes <= 0) {
                        return scheduleDate.setZone('America/New_York').toLocaleString(DateTime.DATETIME_SHORT) + ' ❌';
                    }
                    let timeLeft;
                    if (diffMinutes > 1440) {
                        const days = Math.floor(diffMinutes / 1440);
                        timeLeft = ` (${days}d remaining)`;
                    } else {
                        const hoursLeft = Math.floor(diffMinutes / 60);
                        const minutesLeft = (diffMinutes % 60).toFixed(0);
                        timeLeft = ` (${hoursLeft}h ${minutesLeft}m remaining)`;
                    }
                    return scheduleDate.setZone('America/New_York').toLocaleString(DateTime.DATETIME_SHORT) + timeLeft;
                },
            },
            {
                header: '담당자',
                accessorKey: 'claimed_by',
            },

            {
                id: 'actions',
                header: '',
                cell: (props) => (
                  <ActionsCell
                    {...props}
                    onEdit={(row) => {
                        setEditingTask(row);
                        setEditDialogOpen(true);
                    }}
                    onClaim={(row) => {
                        handleClaim(row);
                    }}
                    onComplete={(row) => {
                      handleComplete(row);
                    }}
                    onUnclaim={(row) => {
                        handleUnclaim(row);
                    }}
                    currentUser={currentUser}
                  />
                ),
            }
        ],
        [currentUser, navigate]
    );
    
    const table = useReactTable({
        data: tasks,
        columns,
        state: {
            sorting,
            lastUpdate,
        },
        onSortingChange: setSorting,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        filterFns: {
            custom: () => true,
        },
        getFacetedRowModel: getFacetedRowModel(),
        getFacetedUniqueValues: getFacetedUniqueValues(),
        getFacetedMinMaxValues: getFacetedMinMaxValues(),
    });

    const fetchTasks = async () => {
        try {
          setIsLoading(true);
          const token = getCookie('_auth');
          
          const response = await Axios.get(
            `${process.env.REACT_APP_URL}/api/v1/tasks/inprogress`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );
          
          setTasks(response.data);
          setError(null);
        } catch (error) {
          console.error('[Fetch] Error:', error);
          setError('Failed to fetch tasks');
        } finally {
          setIsLoading(false);
        }
      };
    
    const fetchCompletedTasks = async () => {
        try {
            const token = getCookie('_auth');
            const response = await Axios.get(
                `${process.env.REACT_APP_URL}/api/v1/tasks/completed`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            setCompletedTasks(response.data);
        } catch (error) {
            console.error('[Fetch Completed] Error:', error);
            setError('Failed to fetch completed tasks');
        }
    };

    const handleClaim = async (row) => {
        try {
          const token = getCookie('_auth');
          const res = await Axios.patch(
            `${process.env.REACT_APP_URL}/api/v1/tasks/claim/${row.id}/${row.student_id}`,
            {},
            {
              headers: {
                Authorization: `Bearer ${token}`,
              }
            }
          );
          if(res.status === 200){
            setAction(prev => prev + 1);
          }
        } catch (error) {
          console.error('[Claim] Error:', error);   
          setError('Failed to claim task');
        }
      };
    
    const handleUnclaim = async (row) => {
        try {
            const token = getCookie('_auth');
            const res = await Axios.patch(
                `${process.env.REACT_APP_URL}/api/v1/tasks/unclaim/${row.id}/${row.student_id}`,
                {},
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            );
            if(res.status === 200){
                setAction(prev => prev - 1);
            }
        } catch (error) {
            console.error('[Unclaim] Error:', error);
            setError('Failed to unclaim task');
        }
    };

    const handleComplete = async (row) => {
        try {
            const token = getCookie('_auth');
            const res = await Axios.patch(
                `${process.env.REACT_APP_URL}/api/v1/tasks/complete/${row.id}/${row.student_id}`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            if(res.status === 200){
                setAction(prev => prev - 1);
                setActionCompleted(prev => prev + 1);
            }
        } catch (error) {
            console.error('[Complete] Error:', error);
            setError('Failed to complete task');
        }
    }

    const EditDialog = () => {
        const now = DateTime.now().setZone('America/New_York');
        const firstEmailSchedule = DateTime.fromISO(editingTask?.requested_at).setZone('America/New_York').plus({ days: 1 }).set({ hour: 22, minute: 30, second: 0 });
        const secondEmailSchedule = DateTime.fromISO(editingTask?.requested_at).setZone('America/New_York').plus({ days: 7 }).set({ hour: 22, minute: 30, second: 0 });
        return (
            <Dialog 
                open={editDialogOpen} 
                onClose={handleEditClose}
                maxWidth="sm"
                fullWidth
            >
                <DialogContent>
                    <Typography variant="h5" sx={{ mb: 3 }}>
                        Edit Email Schedule
                    </Typography>
                    {editingTask && (
                        <Stack spacing={3}>
                            <Alert severity="info" sx={{ mt: 2 }}>
                                <AlertTitle>Note</AlertTitle>
                                Once a reminder email has been sent, its schedule cannot be modified. 
                                You can only modify the schedule for reminder emails that haven't been sent yet.
                            </Alert>
                            <Box>
                                <FormControl fullWidth disabled={editingTask.first_email_sent_at !== null || now > firstEmailSchedule}>
                                    <InputLabel>First Email Schedule</InputLabel>
                                    <Select
                                        value={editingTask.first_email_scheduled ? 'scheduled' : 'disabled'}
                                        label="First Email Schedule"
                                        onChange={(e) => {
                                            setEditingTask(prev => ({
                                                ...prev,
                                                first_email_scheduled: e.target.value === 'scheduled'
                                            }));
                                        }}
                                    >
                                        <MenuItem value="scheduled">Scheduled</MenuItem>
                                        <MenuItem value="disabled">Disabled</MenuItem>
                                    </Select>
                                </FormControl>
                                <Typography 
                                    variant="body2" 
                                    color="text.secondary" 
                                    sx={{ mt: 1, ml: 1 }}
                                >
                                    {editingTask.first_email_sent_at ? (
                                        `First reminder email was already sent at ${DateTime.fromISO(editingTask.first_email_sent_at).setZone('America/New_York').toLocaleString(DateTime.DATETIME_SHORT)}`
                                    ) : (
                                        "First reminder email will be sent at 10:30 PM ET the day after the task is created. Disable this to prevent the email from being sent."
                                    )}
                                </Typography>
                            </Box>
                            
                            <Box>
                                <FormControl fullWidth disabled={editingTask.second_email_sent_at !== null || now > secondEmailSchedule}>
                                    <InputLabel>Second Email Schedule</InputLabel>
                                    <Select
                                        value={editingTask.second_email_scheduled ? 'scheduled' : 'disabled'}
                                        label="Second Email Schedule"
                                        onChange={(e) => {
                                            setEditingTask(prev => ({
                                                ...prev,
                                                second_email_scheduled: e.target.value === 'scheduled'
                                            }));
                                        }}
                                    >
                                        <MenuItem value="scheduled">Scheduled</MenuItem>
                                        <MenuItem value="disabled">Disabled</MenuItem>
                                    </Select>
                                </FormControl>
                                <Typography 
                                    variant="body2" 
                                    color="text.secondary" 
                                    sx={{ mt: 1, ml: 1 }}
                                >
                                    {editingTask.second_email_sent_at ? (
                                        `Second reminder email was already sent at ${DateTime.fromISO(editingTask.second_email_sent_at).setZone('America/New_York').toLocaleString(DateTime.DATETIME_SHORT)}`
                                    ) : (
                                        "Second reminder email will be sent at 10:30 PM ET one week after the task is created. Disable this to prevent the email from being sent."
                                    )}
                                </Typography>
                            </Box>


                        </Stack>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleEditClose}>Cancel</Button>
                    <Button onClick={handleEditSave} variant="contained" disabled={editingTask?.first_email_sent_at !== null && editingTask?.second_email_sent_at !== null}>
                        Save Changes
                    </Button>
                </DialogActions>
            </Dialog>
        );
    };

    const filteredRows = table.getRowModel().rows.filter(row => {
        if (inquiryTypeFilter !== 'all') {
            const isReInquiry = row.original.memo_id !== null && row.original.is_info_mail === false;
            const isNewInquiry = row.original.info_mail_id !== null && row.original.is_info_mail === true;
            if (inquiryTypeFilter === '재문의' && !isReInquiry) return false;
            if (inquiryTypeFilter === '신규문의' && !isNewInquiry) return false;
        }

        if (showPriorityOnly && !row.original.is_priority) return false;

        if (claimStatusFilter !== 'all') {
            const isClaimed = row.original.claimed_by !== null;
            if (claimStatusFilter === 'claimed' && !isClaimed) return false;
            if (claimStatusFilter === 'unclaimed' && isClaimed) return false;
        }

        return true;
    });

    return (
        <Box sx={{ padding: 6 }}>
            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}
            
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 4 }}>
                <Tabs 
                    value={activeTab} 
                    onChange={(e, newValue) => setActiveTab(newValue)}
                    sx={{ '& .MuiTab-root': { fontSize: '1.1rem' } }}
                >
                    <Tab label="Scheduled Tasks" value="scheduled" />
                    <Tab label="Completed Tasks" value="completed" />
                    <Tab label="Follow-Ups" value="followups" />
                </Tabs>
            </Box>

            {activeTab === 'scheduled' && (
                <>
                    <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
                        <FormControl size="small" sx={{ minWidth: 200 }}>
                            <InputLabel>Inquiry Type</InputLabel>
                            <Select
                                value={inquiryTypeFilter}
                                onChange={(e) => setInquiryTypeFilter(e.target.value)}
                                label="Inquiry Type"
                            >
                                <MenuItem value="all">전체</MenuItem>
                                <MenuItem value="재문의">재문의</MenuItem>
                                <MenuItem value="신규문의">신규문의</MenuItem>
                            </Select>
                        </FormControl>


                        <FormControl size="small" sx={{ minWidth: 200 }}>
                            <InputLabel>Claim Status</InputLabel>
                            <Select
                                value={claimStatusFilter}
                                onChange={(e) => setClaimStatusFilter(e.target.value)}
                                label="Claim Status"
                            >
                                <MenuItem value="all">All</MenuItem>
                                <MenuItem value="claimed">Assigned</MenuItem>
                                <MenuItem value="unclaimed">Unassigned</MenuItem>
                            </Select>
                        </FormControl>

                        <Button 
                            variant={showPriorityOnly ? "contained" : "outlined"}
                            onClick={() => setShowPriorityOnly(!showPriorityOnly)}
                            color="primary"
                        >
                            Priority Only
                        </Button>

                        <Button 
                            variant="outlined"
                            onClick={() => {
                                setSorting(prev => [{
                                    id: 'requested_at',
                                    desc: !prev[0]?.desc
                                }]);
                            }}
                        >
                            Sort Date {!sorting[0]?.desc ? '↑' : '↓'}
                        </Button>
                    </Stack>

                    {isLoading && tasks.length === 0 ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <Paper sx={{ 
                            width: '100%', 
                            overflow: 'visible',
                            mb: 6,
                            boxShadow: 3,
                            '& .MuiTableHead-root': {
                                '& .MuiTableCell-root': {
                                    color: 'black',
                                    fontWeight: 'bold',
                                }
                            }
                        }}>
                            <TableContainer>
                                <Table>
                                    <TableHead>
                                        {table.getHeaderGroups().map(headerGroup => (
                                            <TableRow key={headerGroup.id}>
                                                {headerGroup.headers.map(header => (
                                                    <TableCell 
                                                        key={header.id}
                                                        sx={{ fontSize: '1rem' }}
                                                    >
                                                        {flexRender(
                                                            header.column.columnDef.header,
                                                            header.getContext()
                                                        )}
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        ))}
                                    </TableHead>
                                    <TableBody>
                                        {filteredRows.map(row => (
                                            <TableRow key={row.id}>
                                                {row.getVisibleCells().map(cell => (
                                                    <TableCell 
                                                        key={cell.id}
                                                        sx={{ fontSize: '1rem' }}
                                                    >
                                                        {flexRender(
                                                            cell.column.columnDef.cell,
                                                            cell.getContext()
                                                        )}
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>
                    )}
                </>
            )}
            {activeTab === 'completed' && (
                <CompletedTaskDashboard
                    tasks={completedTasks}
                    isLoading={isLoading}
                    errorMessage={error}
                    action={action}
                    setAction={setAction}
                    actionCompleted={actionCompleted}
                    setActionCompleted={setActionCompleted}
                />
            )}
            {activeTab === 'followups' && (
                <FollowUpDashboard />
            )}
            <EditDialog />
            <EmailPreviewDialog />
        </Box>
    );
}